(() => {
  window.LumbvHomeController?.destroy();

  const lifecycle = new AbortController();
  const instances = new Map();

  /** Replace translated placeholders without interpreting merchant text as markup. */
  const interpolate = (text, values) => {
    let result = text;
    for (const [key, value] of Object.entries(values)) {
      result = result.replaceAll(`__${key.toUpperCase()}__`, String(value));
      result = result.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), String(value));
    }
    return result;
  };

  const collect = (root, selector) => {
    const elements = [...root.querySelectorAll(selector)];
    if (root instanceof Element && root.matches(selector)) elements.unshift(root);
    return elements;
  };

  const createMenu = (menu) => {
    const controller = new AbortController();
    const { signal } = controller;

    menu.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('nav a')) menu.open = false;
    }, { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.open) {
        menu.open = false;
        menu.querySelector('summary')?.focus();
      }
    }, { signal });
    document.addEventListener('click', (event) => {
      if (event.target instanceof Node && !menu.contains(event.target)) menu.open = false;
    }, { signal });

    return () => controller.abort();
  };

  const createShop = (section) => {
    const dataElement = section.querySelector('[data-handset-data]');
    const form = section.querySelector('[data-handset-form]');
    const nativeOptions = section.querySelector('[data-native-options]');
    const enhancedOptions = section.querySelector('[data-enhanced-options]');
    const addButton = section.querySelector('[data-add-button]');
    const addLabel = section.querySelector('[data-add-label]');
    const addPrice = section.querySelector('[data-add-price]');
    const totalGroup = section.querySelector('.lh-shop__total');
    const totalElement = section.querySelector('[data-pack-total]');
    const listElement = section.querySelector('[data-pack-list]');
    const statusElement = section.querySelector('[data-form-status]');
    const image = section.querySelector('[data-shop-image]');
    const packInputs = [...section.querySelectorAll('input[data-pack]')];
    const thumbs = [...section.querySelectorAll('button[data-colour-preview]')];
    const rows = [...section.querySelectorAll('[data-handset-row]')].map((element) => ({
      element,
      connector: element.querySelector('select[data-connector]'),
      colour: element.querySelector('select[data-colour]'),
    }));

    if (!(form instanceof HTMLFormElement) || !(addButton instanceof HTMLButtonElement)
      || !dataElement || !nativeOptions || !enhancedOptions || !addLabel || !addPrice
      || !totalGroup || !totalElement || !listElement || !statusElement || rows.length !== 3
      || rows.some((row) => !(row.connector instanceof HTMLSelectElement)
        || !(row.colour instanceof HTMLSelectElement))
      || ![1, 2, 3].every((count) => packInputs.some((input) => Number(input.value) === count))) return;

    let data;
    let money;
    let addUrl;
    let cartUrl;
    try {
      data = JSON.parse(dataElement.textContent);
      if (!Array.isArray(data.variants) || !data.variants.length
        || !data.variants.every((variant) => Number.isSafeInteger(variant.id)
          && Number.isFinite(variant.price) && variant.price >= 0 && Array.isArray(variant.options))
        || !['addToCart', 'adding', 'added', 'error', 'unavailable'].every((key) => typeof data.strings?.[key] === 'string')) return;
      money = new Intl.NumberFormat(data.locale || undefined, { style: 'currency', currency: data.currency });
      addUrl = new URL(data.cartAddUrl, window.location.origin);
      cartUrl = new URL(data.cartUrl, window.location.origin);
      if (addUrl.origin !== window.location.origin || cartUrl.origin !== window.location.origin) return;
      if (!addUrl.pathname.endsWith('.js')) addUrl.pathname = `${addUrl.pathname.replace(/\/$/, '')}.js`;
    } catch {
      // Leave the server-rendered product and quantity selectors usable.
      return;
    }

    const connectorIndex = Number(data.connectorPosition) - 1;
    const colourIndex = Number(data.colourPosition) - 1;
    if (!Number.isInteger(connectorIndex) || !Number.isInteger(colourIndex)
      || connectorIndex < 0 || colourIndex < 0 || connectorIndex === colourIndex) return;

    const controller = new AbortController();
    const { signal } = controller;
    const nativeId = nativeOptions.querySelector('select[name="id"]');
    const nativeQuantity = nativeOptions.querySelector('select[name="quantity"]');
    const originalLabel = addLabel.textContent;
    const originalDisabled = addButton.disabled;
    const controls = [...enhancedOptions.querySelectorAll('input, select, button'), ...thumbs];
    const originalControlStates = new Map(controls.map((control) => [control, control.disabled]));
    let submitting = false;

    const currency = (cents) => money.format(cents / 100);
    const selectedPack = () => Number(packInputs.find((input) => input.checked)?.value) || 2;
    const findVariant = (row) => data.variants.find((variant) => variant.options[connectorIndex] === row.connector.value
      && variant.options[colourIndex] === row.colour.value);

    const selectedVariants = () => rows.slice(0, selectedPack()).map(findVariant);
    const pricePack = (variants) => {
      const sum = variants.reduce((total, variant) => total + variant.price, 0);
      const cheapest = Math.min(...variants.map((variant) => variant.price));
      const discount = variants.length === 2 ? Math.round(cheapest / 2) : variants.length === 3 ? cheapest : 0;
      return { sum, total: sum - discount };
    };
    const setStatus = (message, state = '') => {
      statusElement.textContent = message;
      statusElement.dataset.state = state;
    };
    const setControlsBusy = (busy) => {
      for (const control of controls) control.disabled = busy || originalControlStates.get(control);
      form.setAttribute('aria-busy', String(busy));
    };

    const renderImage = (variant) => {
      const colour = rows[0].colour.value;
      const matchingImage = variant?.image || data.variants.find((candidate) => candidate.options[colourIndex] === colour && candidate.image)?.image;
      if (image instanceof HTMLImageElement && matchingImage?.url) {
        const next = new URL(matchingImage.url, window.location.origin).href;
        if (image.src !== next) {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = next;
        }
        if (typeof matchingImage.alt === 'string') image.alt = matchingImage.alt;
      }
      for (const thumb of thumbs) thumb.setAttribute('aria-pressed', String(thumb.dataset.colourPreview === colour));
    };

    const render = () => {
      const count = selectedPack();
      const variants = selectedVariants();
      const valid = variants.length === count && variants.every((variant) => variant?.available === true);
      const first = findVariant(rows[0]);

      for (const [index, row] of rows.entries()) {
        row.element.hidden = index >= count;
        const unavailable = index < count && findVariant(row)?.available !== true;
        row.element.classList.toggle('is-unavailable', unavailable);
        for (const select of [row.connector, row.colour]) {
          if (unavailable) select.setAttribute('aria-invalid', 'true');
          else select.removeAttribute('aria-invalid');
        }
      }

      for (const input of packInputs) {
        const label = input.closest('.lh-pack');
        const quantity = Number(input.value);
        label?.classList.toggle('is-selected', input.checked);
        const price = label?.querySelector('[data-pack-price]');
        const compare = label?.querySelector('[data-pack-compare]');
        if (first && price) price.textContent = currency(pricePack(Array(quantity).fill(first)).total);
        if (first && compare) {
          compare.textContent = currency(first.price * quantity);
          compare.hidden = quantity === 1;
        }
      }

      if (variants.every(Boolean)) {
        const price = pricePack(variants);
        totalElement.textContent = currency(price.total);
        listElement.textContent = currency(price.sum);
        listElement.hidden = count === 1;
        addPrice.textContent = currency(price.total);
      } else {
        totalElement.textContent = '—';
        listElement.hidden = true;
        addPrice.textContent = '—';
      }

      if (nativeId instanceof HTMLSelectElement && first) nativeId.value = String(first.id);
      if (nativeQuantity instanceof HTMLSelectElement) nativeQuantity.value = String(count);
      if (data.syncVariantUrl === true && first && !submitting) {
        const nextUrl = new URL(window.location.href);
        if (nextUrl.searchParams.get('variant') !== String(first.id)) {
          nextUrl.searchParams.set('variant', String(first.id));
          window.history.replaceState(window.history.state, '', nextUrl.href);
        }
      }
      addButton.disabled = submitting || !valid;
      addLabel.textContent = submitting ? data.strings.adding : valid
        ? interpolate(data.strings.addToCart, { count }) : data.strings.unavailable;
      if (!submitting) setStatus(valid ? '' : data.strings.unavailable, valid ? '' : 'error');
      renderImage(first);
    };

    enhancedOptions.addEventListener('change', (event) => {
      if (event.target instanceof Element && event.target.matches('[data-pack], [data-connector], [data-colour]')) render();
    }, { signal });

    for (const thumb of thumbs) {
      thumb.addEventListener('click', () => {
        const colour = thumb.dataset.colourPreview;
        if (submitting || ![...rows[0].colour.options].some((option) => option.value === colour)) return;
        // Previewing a colour only changes handset one; preserve other choices.
        rows[0].colour.value = colour;
        render();
      }, { signal });
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (submitting) return;
      const variants = selectedVariants();
      if (variants.length !== selectedPack() || variants.some((variant) => variant?.available !== true)) {
        render();
        return;
      }

      const quantities = new Map();
      for (const variant of variants) quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1);
      const items = [...quantities].map(([id, quantity]) => ({ id, quantity }));
      submitting = true;
      setControlsBusy(true);
      render();
      setStatus(data.strings.adding);

      try {
        const response = await fetch(addUrl.href, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
          signal,
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || Number(result?.status) >= 400 || !Array.isArray(result?.items) || !result.items.length) {
          throw new Error(typeof result?.description === 'string' ? result.description : data.strings.error);
        }
        setStatus(data.strings.added, 'success');
        window.location.assign(cartUrl.href);
      } catch (error) {
        if (signal.aborted) return;
        submitting = false;
        setControlsBusy(false);
        render();
        setStatus(error instanceof Error ? error.message : data.strings.error, 'error');
      }
    }, { signal });

    if (!packInputs.some((input) => input.checked)) packInputs.find((input) => Number(input.value) === 2).checked = true;
    render();
    // Enhance only after data, prices and event handlers are ready. Native inputs
    // remain enabled for a valid normal form if enhancement is later removed.
    nativeOptions.hidden = true;
    enhancedOptions.hidden = false;
    totalGroup.hidden = false;
    addPrice.hidden = false;
    section.dataset.handsetEnhanced = 'true';

    return () => {
      controller.abort();
      setControlsBusy(false);
      nativeOptions.hidden = false;
      enhancedOptions.hidden = true;
      totalGroup.hidden = true;
      addPrice.hidden = true;
      addLabel.textContent = originalLabel;
      addButton.disabled = originalDisabled;
      form.removeAttribute('aria-busy');
      delete section.dataset.handsetEnhanced;
    };
  };

  const initialize = (root = document) => {
    for (const [element, destroy] of instances) {
      if (!element.isConnected) {
        destroy();
        instances.delete(element);
      }
    }
    for (const [selector, create] of [['.lh-mobile-menu', createMenu], ['.lh-shop', createShop]]) {
      for (const element of collect(root, selector)) {
        instances.get(element)?.();
        const destroy = create(element);
        if (destroy) instances.set(element, destroy);
        else instances.delete(element);
      }
    }
  };

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) initialize(event.target);
  }, { signal: lifecycle.signal });
  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;
    for (const [element, destroy] of instances) {
      if (element === event.target || event.target.contains(element)) {
        destroy();
        instances.delete(element);
      }
    }
  }, { signal: lifecycle.signal });

  window.LumbvHomeController = {
    destroy() {
      lifecycle.abort();
      for (const destroy of instances.values()) destroy();
      instances.clear();
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true, signal: lifecycle.signal });
  } else initialize();
})();
